import { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';

interface GridCitationSelectionToolbarProps {
  count: number;
  canAccept: boolean;
  canApply: boolean;
  busy?: boolean;
  onAccept: () => void;
  onApply: () => void;
  onReject: () => void;
  onClear: () => void;
  onFilterDrawer: () => void;
  onCancel: () => void;
}

const GridCitationSelectionToolbar = ({
  count,
  canAccept,
  canApply,
  busy = false,
  onAccept,
  onApply,
  onReject,
  onClear,
  onFilterDrawer,
  onCancel,
}: GridCitationSelectionToolbarProps) => {
  const [confirmClear, setConfirmClear] = useState(false);
  const empty = count === 0;

  return (
    <>
      <Stack
        role="toolbar"
        aria-label="Selected cell citations"
        data-testid="grid-citation-selection-toolbar"
        onFocusCapture={event => {
          // Native focus can leave an action partly clipped in the horizontal scroller.
          event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }}
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          maxWidth: 'min(60vw, 760px)',
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '& > *': { flexShrink: 0, whiteSpace: 'nowrap' },
          '& .MuiButton-root': { minHeight: 24, py: 0 },
        }}>
        <Typography variant="caption">{count} selected</Typography>
        <Button size="small" variant="contained" disabled={busy || !canAccept} onClick={onAccept}>
          Accept
        </Button>
        <Button
          size="small"
          variant={canApply ? 'contained' : 'outlined'}
          disabled={busy || !canApply}
          onClick={onApply}>
          Apply citation
        </Button>
        <Button size="small" disabled={busy || empty} onClick={onReject}>
          Reject citations
        </Button>
        <Button size="small" disabled={busy || empty} onClick={() => setConfirmClear(true)}>
          Clear citations
        </Button>
        <Button size="small" disabled={busy || empty} onClick={onFilterDrawer}>
          Filter review
        </Button>
        <Button size="small" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
      <ConfirmationDialog
        open={confirmClear}
        title="Clear citations"
        onClose={() => setConfirmClear(false)}
        actions={[
          { label: 'Cancel', variant: 'text' },
          {
            label: 'Confirm',
            variant: 'contained',
            callback: () => {
              setConfirmClear(false);
              onClear();
            },
          },
        ]}>
        Cell values remain. Selected citations and Excel references will be permanently deleted.
      </ConfirmationDialog>
    </>
  );
};

export default GridCitationSelectionToolbar;
